import moment from 'moment';
import { Formik } from 'formik';
import classNames from 'classnames';
import pinterpolate from 'pinterpolate';
import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { getFormattedDate } from 'utils/date';
import { extractFullName, interpolate } from 'utils/string';

import {
  useCreateResignationMutation,
  useDeleteResignationMutation,
  useFetchResignationQuery,
  useFetchUserByIdQuery,
  useUpdateResignationMutation,
} from 'hooks/query/useUserQueries';

import useTitle from 'hooks/useTitle';
import { useUserPermissions } from 'hooks/query/useUserInfo';

import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';
import InputWrapper from 'components/common/inputWrapper';
import BackButton from 'components/common/button/BackButton';
import DatePicker from 'components/common/datepicker/DatePicker';

import ProfileHeader from '../components/profileHeader/ProfileHeader';
import EmployeeHistoryList from '../../components/EmploymentHistoryList';

import {
  REACT_DATEPICKER_DATE_FORMAT,
  EMPLOYEE_RESIGNATION_DELETE_CONFIRMATION_TITLE,
  EMPLOYEE_RESIGNATION_DELETE_CONFIRMATION_MESSAGE,
} from 'constants/appConstants';
import * as routes from 'constants/routes';
import { EMPLOYEES } from 'constants/permissions';

import { changeTentativeExitSchema } from 'schemas/changeTentativeExitSchema';
import type { ChangeTentativeExit as ChangeTentativeExitPayload } from 'interface/tentativeExit';
import type { EmployeeLike } from 'interface/userUtils';

interface ChangeTentativeExitProps extends RouteComponentProps<{ id?: string }> {
  user: EmployeeLike;
  match: { params: { id?: string } };
}

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  message: { text: string };
  title: { text: string; type: string };
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

type HeaderEmployee = React.ComponentProps<typeof ProfileHeader>['employee'] & {
  joinDate?: string;
  empStatusHistory?: Array<{ transitionDate: string }>;
  designationAreaHistory?: Array<{ transitionDate: string }>;
  designation?: { name: string; area?: { name: string } };
};
type EmployeeHistoryListEmployee = React.ComponentProps<typeof EmployeeHistoryList>['employee'];

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  message,
  title,
  onConfirm,
  onCancel,
  isDeleting,
}) => (
  <LFModal
    isOpen={isOpen}
    renderFooter={true}
    title={title}
    className="delete-modal"
    customBtnClass="hidden"
    message={message}
    buttons={[
      {
        text: 'Yes, delete',
        type: 'red',
        className: 'mr-12 delete__btn',
        close: true,
        showLoading: true,
        isLoading: isDeleting,
        onClick: onConfirm,
      },
      {
        text: 'Cancel',
        type: 'outlined-grey',
        className: 'py-8 px-12',
        onClick: !isDeleting && onCancel,
      },
    ]}
  />
);

const ChangeTentativeExit: React.FC<ChangeTentativeExitProps> = ({ user, match: { params } }) => {
  const permissions = useUserPermissions() as Record<string, boolean>;
  const userId = Number(params.id);

  const { data: employeeData, isLoading: isLoadingEmployee } = useFetchUserByIdQuery<HeaderEmployee>(
    userId,
    Boolean(userId)
  );
  const { data: resignationResponse, isLoading: isLoadingResignation } = useFetchResignationQuery<{
    data: ChangeTentativeExitPayload & { id?: number };
  }>(userId, Boolean(userId));
  const createResignationMutation = useCreateResignationMutation();
  const updateResignationMutation = useUpdateResignationMutation();
  const deleteResignationMutation = useDeleteResignationMutation();

  const employee = employeeData ?? { id: 0, firstName: '', lastName: '' };
  const empResignation = resignationResponse?.data ?? null;
  const isSubmitting = createResignationMutation.isPending || updateResignationMutation.isPending;
  const isDeleting = deleteResignationMutation.isPending;
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);

  const canEditResignation = permissions[EMPLOYEES.RESIGNATIONS.UPDATE];
  const canCreateResignation = permissions[EMPLOYEES.RESIGNATIONS.CREATE];
  const canDeleteResignation = permissions[EMPLOYEES.RESIGNATIONS.DELETE];
  const isEditMode = Boolean(empResignation?.id);

  const setTitle = useTitle('Employee Resignation');

  const getPageTitle = (employeeData: HeaderEmployee | null) => {
    const employeeName = employeeData ? extractFullName(employeeData) : '';
    const pageTitle = employeeName ? `${employeeName}'s resignation` : 'Employee Resignation';

    return pageTitle;
  };

  useEffect(() => {
    if (employeeData) {
      setTitle(getPageTitle(employeeData));
    }
  }, [employeeData, setTitle]);

  const getInitialValues = (resignation: (ChangeTentativeExitPayload & { id?: number }) | null) => {
    return {
      reason: resignation?.reason || '',
      tentativeExitDate: resignation?.tentativeExitDate || null,
      informedDate: resignation?.informedDate || null,
    };
  };

  const initialValues = getInitialValues(empResignation);

  const handleSubmit = ({ reason, tentativeExitDate, informedDate }: ChangeTentativeExitPayload) => {
    tentativeExitDate = getFormattedDate(tentativeExitDate);
    informedDate = getFormattedDate(informedDate);

    if (isEditMode && empResignation?.id) {
      updateResignationMutation.mutate(
        {
          resignation: { reason, tentativeExitDate, informedDate },
          userId,
          id: empResignation.id,
        },
        {
          onSuccess: () => {
            redirectToEmployeeProfile();

            toast.success({
              title: 'Success',
              message: 'Tentative exit date has been updated successfully.',
            });
          },
        }
      );
    } else {
      createResignationMutation.mutate(
        {
          resignation: { reason, tentativeExitDate, informedDate },
          userId,
        },
        {
          onSuccess: () => {
            redirectToEmployeeProfile();

            toast.success({
              title: 'Success',
              message: 'Tentative exit date has been added successfully',
            });
          },
        }
      );
    }
  };

  const onDelete = () => {
    if (isEditMode && empResignation?.id) {
      deleteResignationMutation.mutate(
        { userId, id: empResignation.id },
        {
          onSuccess: () => {
            setIsDeleteConfirmationModalOpen(false);

            redirectToEmployeeProfile();

            toast.success({
              title: 'Success',
              message: 'Tentative exit date has been deleted successfully.',
            });
          },
        }
      );
    }
  };

  const redirectToEmployeeProfile = () => {
    history.push(interpolate(routes.EMPLOYEE_PROFILE, { id: userId }));
  };

  const showDeleteConfirmationModal = () => {
    setIsDeleteConfirmationModalOpen(true);
  };

  const closeDeleteConfirmationModal = () => {
    setIsDeleteConfirmationModalOpen(false);
  };

  if (isLoadingEmployee || isLoadingResignation) {
    return (
      <div className="container loading-container">
        <Loading />
      </div>
    );
  }

  const historyEmployee: EmployeeHistoryListEmployee = {
    empStatus: employee.empStatus || '',
    joinDate: employee.joinDate || '',
    empStatusHistory: employee.empStatusHistory as EmployeeHistoryListEmployee['empStatusHistory'],
    designationAreaHistory: employee.designationAreaHistory as EmployeeHistoryListEmployee['designationAreaHistory'],
    designation: employee.designation,
  };

  return (
    <>
      <DeleteConfirmationModal
        isOpen={isDeleteConfirmationModalOpen}
        title={{
          text: EMPLOYEE_RESIGNATION_DELETE_CONFIRMATION_TITLE,
          type: `danger`,
        }}
        message={{
          text: pinterpolate(EMPLOYEE_RESIGNATION_DELETE_CONFIRMATION_MESSAGE, {
            firstName: employee.firstName,
            lastName: employee.lastName,
          }),
        }}
        onConfirm={onDelete}
        onCancel={closeDeleteConfirmationModal}
        isDeleting={isDeleting}
      />

      <div className="title profile-wrapper">
        <ProfileHeader employee={employee} user={{ id: Number(user.id) }} />
      </div>
      <div className="container d-flex fd-row flex-fix name-wrap change-tentative-exit">
        <div className="full-scope-card momr-20 change-tentative-exit__form-container">
          <div className="change-tentative-exit__title name-wrap">
            <BackButton />
            <h3>{!isEditMode ? 'Add' : 'Edit'} Tentative Exit Date</h3>
          </div>

          <Formik
            key={JSON.stringify(initialValues)}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={changeTentativeExitSchema}
          >
            {({ handleSubmit, errors, values, dirty, setFieldValue, touched, setFieldTouched }) => (
              <form onSubmit={handleSubmit} className="change-tentative-exit__form">
                <div className="change-tentative-exit__form-body">
                  <InputWrapper
                    isMandatory
                    label="Resignation email received on."
                    error={touched.informedDate && errors.informedDate}
                  >
                    <DatePicker
                      date={values.informedDate ? moment(values.informedDate) : null}
                      placeholderText="Select Date"
                      dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                      displayFormat="LL"
                      hasError={Boolean(touched.informedDate && errors.informedDate)}
                      openDirection="up"
                      onDateChange={(selectedDate) => {
                        setFieldTouched('informedDate', true, true);
                        setFieldValue('informedDate', selectedDate);
                      }}
                      disabled={isSubmitting}
                    />
                  </InputWrapper>

                  <InputWrapper
                    isMandatory
                    label="Tentative exit date"
                    error={touched.tentativeExitDate && errors.tentativeExitDate}
                  >
                    <DatePicker
                      date={values.tentativeExitDate ? moment(values.tentativeExitDate) : null}
                      placeholderText="Select Date"
                      dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                      displayFormat="LL"
                      hasError={Boolean(touched.tentativeExitDate && errors.tentativeExitDate)}
                      openDirection="up"
                      onDateChange={(selectedDate) => {
                        setFieldTouched('tentativeExitDate', true, true);
                        setFieldValue('tentativeExitDate', selectedDate);
                      }}
                      disabled={isSubmitting}
                    />
                  </InputWrapper>

                  <InputWrapper isMandatory label="Reason" error={touched.reason && errors.reason}>
                    <textarea
                      name="reason"
                      placeholder="Type your reason..."
                      className={classNames(
                        'form-elem font-weight-regular custom-scroll-bar change-issuer-request__reason',
                        {
                          'form-elem--error': touched.reason && errors.reason,
                        }
                      )}
                      value={values.reason}
                      onChange={(e) => {
                        setFieldTouched('reason', true, true);
                        setFieldValue('reason', e.target.value);
                      }}
                      disabled={isSubmitting}
                    />
                  </InputWrapper>
                </div>

                <div className="change-tentative-exit__footer">
                  {isEditMode && canEditResignation && (
                    <button
                      type="submit"
                      className={classNames('btn card-button change-tentative-exit__btn', {
                        'btn--primary': !isSubmitting && dirty,
                        'btn--disabled': isSubmitting || !dirty,
                      })}
                      disabled={isSubmitting || !dirty}
                    >
                      <span className={classNames({ invisible: isSubmitting })}>Save</span>
                      {isSubmitting && <Loading />}
                    </button>
                  )}

                  {!isEditMode && canCreateResignation && (
                    <button
                      type="submit"
                      className={classNames('btn btn--primary card-button change-tentative-exit__btn', {
                        'btn--primary': !isSubmitting,
                        'btn--disabled': isSubmitting,
                      })}
                      disabled={isSubmitting}
                    >
                      <span className={classNames({ invisible: isSubmitting })}>Add</span>
                      {isSubmitting && <Loading />}
                    </button>
                  )}

                  <button
                    type="button"
                    className={classNames('btn card-button ml-10 change-tentative-exit__btn', {
                      'btn--outlined-grey': !isSubmitting,
                      'btn--disabled': isSubmitting,
                    })}
                    onClick={() => redirectToEmployeeProfile()}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>

                  {isEditMode && canDeleteResignation && (
                    <button
                      type="button"
                      className="btn btn--outlined-danger f-right card-button change-tentative-exit__btn"
                      onClick={showDeleteConfirmationModal}
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </form>
            )}
          </Formik>
        </div>
        <div
          className={classNames(
            'full-scope-card',
            'emp-history-area',
            'emp-history-wrapper',
            'custom-scroll-bar',
            'change-tentative-exit__sidebar'
          )}
        >
          <EmployeeHistoryList employee={historyEmployee} />
        </div>
      </div>
    </>
  );
};

export default ChangeTentativeExit;
